import React, { useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiCalendar,
  FiChevronDown,
  FiChevronRight,
  FiExternalLink,
  FiGlobe,
  FiHome,
  FiMail,
  FiMapPin,
  FiPhone,
  FiUser,
} from "react-icons/fi";
import { FaSchool } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import MyDetailsScreen from "./MyDetailsScreen";

const UserIcon = FiUser;
const PhoneIcon = FiPhone;
const MailIcon = FiMail;
const HomeIcon = FiHome;
const MapPinIcon = FiMapPin;
const GlobeIcon = FiGlobe;
const ExternalIcon = FiExternalLink;
const SchoolIcon = FaSchool;
const CalendarIcon = FiCalendar;
const AlertIcon = FiAlertCircle;
const ChevronDownIcon = FiChevronDown;
const ChevronRightIcon = FiChevronRight;

const getSchoolValue = (parentData, keys, fallback = "") => {
  const schoolSources = [
    parentData?.schoolSettings,
    parentData?.school,
    parentData?.schoolInfo,
    parentData?.schoolDetails,
    parentData?.schoolData,
  ];

  for (const key of keys) {
    const value = schoolSources.map((source) => source?.[key]).find(Boolean);

    if (value) return value;

    if (key.startsWith("school") && parentData?.[key]) {
      return parentData[key];
    }
  }

  return fallback;
};

const parseCalendarDate = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day, hour = 0, minute = 0] = value;
    const date = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === "string") {
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/);
    if (match) {
      const date = new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        Number(match[4] || 0),
        Number(match[5] || 0)
      );
      return Number.isNaN(date.getTime()) ? null : date;
    }
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getDateParts = (value, locale = "en-US") => {
  const date = parseCalendarDate(value);

  if (!date) {
    return { day: "-", weekday: "" };
  }

  return {
    day: String(date.getDate()),
    weekday: date
      .toLocaleDateString(locale, { weekday: "short" })
      .replace(".", "")
      .toLowerCase(),
  };
};

const isMultiDayEvent = (event, formatCalendarDate) => {
  if (!event?.startDate || !event?.endDate) return false;

  if (typeof formatCalendarDate === "function") {
    return formatCalendarDate(event.startDate) !== formatCalendarDate(event.endDate);
  }

  const start = parseCalendarDate(event.startDate);
  const end = parseCalendarDate(event.endDate);

  if (!start || !end) return false;

  return start.toDateString() !== end.toDateString();
};

const InfoItem = ({ icon: Icon, label, value, compact = false }) => {
  if (!value) return null;

  return (
    <div className={`pp-mobile-info-item ${compact ? "is-compact" : ""}`}>
      <span className="pp-mobile-info-icon">
        <Icon aria-hidden="true" />
      </span>
      <div className="pp-mobile-info-text">
        {label && <dt>{label}</dt>}
        <dd>{value}</dd>
      </div>
    </div>
  );
};

const SimpleLine = ({ icon: Icon, children }) => {
  if (!children) return null;

  return (
    <div className="pp-mobile-simple-line">
      <Icon aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
};

const CollapsibleSection = ({
  title,
  subtitle,
  icon: Icon,
  defaultOpen = false,
  index = 0,
  tone = "default",
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section
      className={`pp-mobile-collapsible pp-info-collapsible is-${tone} ${open ? "is-open" : ""}`}
      style={{ "--pp-info-delay": `${Math.min(index, 6) * 38}ms` }}
    >
      <button
        type="button"
        className="pp-mobile-collapse-header"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="pp-mobile-collapse-title-wrap">
          {Icon && (
            <span className="pp-mobile-card-icon" aria-hidden="true">
              <Icon />
            </span>
          )}
          <span>
            <span className="pp-mobile-collapse-title">{title}</span>
            {subtitle && <span className="pp-mobile-collapse-subtitle">{subtitle}</span>}
          </span>
        </span>

        <ChevronDownIcon className="pp-mobile-collapse-chevron" aria-hidden="true" />
      </button>

      {open && <div className="pp-mobile-collapse-panel pp-info-collapse-panel">{children}</div>}
    </section>
  );
};

const getNewsletterLabel = ({ t, status, optedOut }) => {
  if (optedOut) {
    return t("parentPortal.newsletter.opted_out", { defaultValue: "Unsubscribed" });
  }

  if (status === "SUBSCRIBED") {
    return t("parentPortal.newsletter.subscribed", { defaultValue: "Subscribed" });
  }

  if (status === "UNSUBSCRIBED") {
    return t("parentPortal.newsletter.unsubscribed", { defaultValue: "Unsubscribed" });
  }

  if (status === "PENDING") {
    return t("parentPortal.newsletter.pending", { defaultValue: "Pending" });
  }

  return t("parentPortal.newsletter.unknown", { defaultValue: "Unknown" });
};

const getClassroomName = (classroom) =>
  String(
    classroom?.className ||
      classroom?.classroomName ||
      classroom?.name ||
      classroom?.label ||
      classroom?.classroom?.className ||
      classroom?.classroom?.classroomName ||
      classroom?.classroom?.name ||
      classroom?.classroom?.label ||
      ""
  ).trim();

const stripTeacherTitle = (value) =>
  typeof value === "string" ? value.trim().replace(/^(juf|meester)\s+/i, "").trim() : "";

const getTeacherName = (classroom) => {
  const nested = classroom?.classroom || {};
  const teacher =
    classroom?.teacher ||
    classroom?.primaryTeacher ||
    nested?.teacher ||
    nested?.primaryTeacher ||
    (Array.isArray(classroom?.teachers) ? classroom.teachers[0] : null) ||
    (Array.isArray(nested?.teachers) ? nested.teachers[0] : null);

  return stripTeacherTitle(
    classroom?.teacherFirstName ||
      nested?.teacherFirstName ||
      teacher?.firstName ||
      teacher?.givenName ||
      teacher?.firstname ||
      classroom?.teacherName ||
      classroom?.teacherFullName ||
      nested?.teacherName ||
      nested?.teacherFullName ||
      teacher?.displayName ||
      teacher?.fullName ||
      teacher?.name ||
      [teacher?.firstName, teacher?.lastName].filter(Boolean).join(" ")
  );
};

const trimClassroomNumber = (value) => String(value || "").replace(/\s+\d+\s*$/, "").trim();

const getClassroomCalendarLabel = (classroom) => {
  const classroomName = getClassroomName(classroom);
  const teacherName = getTeacherName(classroom);

  if (teacherName && classroomName) return `${trimClassroomNumber(classroomName)} · ${teacherName}`;
  return teacherName || classroomName;
};

const getEventScopeLabel = (t, event, getCalendarClassNames) => {
  const scope = String(event?.scope || "").toUpperCase();

  if (scope === "CLASSROOM") {
    const fromParent = typeof getCalendarClassNames === "function" ? getCalendarClassNames(event) : "";
    const classroomName =
      fromParent ||
      event?.classroomName ||
      event?.classroomLabel ||
      event?.classroom?.className ||
      event?.classroom?.name ||
      (Array.isArray(event?.classrooms)
        ? event.classrooms.map(getClassroomCalendarLabel).filter(Boolean).join(", ")
        : "") ||
      (Array.isArray(event?.classroomNames) ? event.classroomNames.filter(Boolean).join(", ") : "");

    return classroomName || t("parentPortal.calendar.scope_class", { defaultValue: "Class" });
  }

  return t("parentPortal.calendar.scope_school", { defaultValue: "School-wide" });
};

const CalendarEventPreview = ({ event, t, formatCalendarDate, formatCalendarTime, getCalendarClassNames, onOpenCalendar, dateLocale }) => {
  if (!event) return null;

  const startParts = getDateParts(event.startDate, dateLocale);
  const isMultiDay = isMultiDayEvent(event, formatCalendarDate);
  const timeLabel = isMultiDay
    ? `${formatCalendarDate?.(event.startDate) || ""} - ${formatCalendarDate?.(event.endDate) || ""}`
    : formatCalendarTime?.(event);
  const scopeLabel = getEventScopeLabel(t, event, getCalendarClassNames);
  const canOpenCalendar = typeof onOpenCalendar === "function";
  const EventWrapper = canOpenCalendar ? "button" : "div";

  const openCalendarEvent = () => {
    if (canOpenCalendar) {
      onOpenCalendar({ event });
    }
  };

  return (
    <EventWrapper
      type={canOpenCalendar ? "button" : undefined}
      className={`pp-mobile-event-card pp-mobile-clickable-row ${canOpenCalendar ? "is-clickable" : ""}`}
      onClick={canOpenCalendar ? openCalendarEvent : undefined}
    >
      <div className={`pp-mobile-event-date ${isMultiDay ? "is-range" : ""}`} aria-hidden="true">
        {isMultiDay ? (
          <div className="pp-mobile-event-range">
            <span className="pp-mobile-event-range-start">
              {parseCalendarDate(event.startDate)?.toLocaleDateString(dateLocale, {
                day: "numeric",
                month: "short",
              })}
            </span>
            <span className="pp-mobile-event-range-separator">—</span>
            <span className="pp-mobile-event-range-end">
              {parseCalendarDate(event.endDate)?.toLocaleDateString(dateLocale, {
                day: "numeric",
                month: "short",
              })}
            </span>
          </div>
        ) : (
          <>
            <span className="pp-mobile-event-day">{startParts.day}</span>
            <span className="pp-mobile-event-weekday">{startParts.weekday}</span>
          </>
        )}
      </div>

      <div className="pp-mobile-event-main">
        <div className="pp-mobile-event-primary-row">
          <strong>{event.title || "-"}</strong>
          {!isMultiDay && timeLabel && <span className="pp-mobile-event-time">{timeLabel}</span>}
        </div>

        {isMultiDay && timeLabel && (
          <span className="pp-mobile-event-time is-range-label">{timeLabel}</span>
        )}

        <span className="pp-mobile-event-scope">{scopeLabel}</span>

        {event.location && <span className="pp-mobile-event-location">{event.location}</span>}
      </div>

      <ChevronRightIcon className="pp-mobile-item-chevron" aria-hidden="true" />
    </EventWrapper>
  );
};

const UpcomingCard = ({
  t,
  nextCalendarEvent,
  upcomingCalendarEvents,
  formatCalendarDate,
  formatCalendarTime,
  getCalendarClassNames,
  dateLocale,
  actionSubtexts = [],
  totalActions = 0,
  onOpenActions,
  onOpenCalendar,
}) => {
  const events = useMemo(() => {
    const source = Array.isArray(upcomingCalendarEvents) && upcomingCalendarEvents.length
      ? upcomingCalendarEvents
      : nextCalendarEvent
        ? [nextCalendarEvent]
        : [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const weekEnd = new Date(today);
    weekEnd.setDate(weekEnd.getDate() + 7);
    weekEnd.setHours(23, 59, 59, 999);

    return source
      .filter((event) => {
        const start = parseCalendarDate(event?.startDate);
        const end = parseCalendarDate(event?.endDate) || start;

        if (!start) return false;

        return start <= weekEnd && end >= today;
      })
      .sort((left, right) => {
        const leftDate = parseCalendarDate(left?.startDate);
        const rightDate = parseCalendarDate(right?.startDate);

        if (!leftDate && !rightDate) return 0;
        if (!leftDate) return 1;
        if (!rightDate) return -1;

        return leftDate.getTime() - rightDate.getTime();
      })
      .slice(0, 3);
  }, [nextCalendarEvent, upcomingCalendarEvents]);

  return (
    <section className="pp-mobile-upcoming-card">
      {events.length ? (
        <div className="pp-mobile-event-list">
          {events.map((event, index) => (
            <CalendarEventPreview
              key={event?.uuid || event?.id || `${event?.title || "event"}-${index}`}
              event={event}
              t={t}
              formatCalendarDate={formatCalendarDate}
              formatCalendarTime={formatCalendarTime}
              getCalendarClassNames={getCalendarClassNames}
              onOpenCalendar={onOpenCalendar}
              dateLocale={dateLocale}
            />
          ))}
        </div>
      ) : (
        <p className="pp-mobile-empty-state">
          {t("parentPortal.summary.no_upcoming_events", {
            defaultValue: "No activities for the upcoming week.",
          })}
        </p>
      )}
    </section>
  );
};

const ActionsCard = ({ t, actionSubtexts = [], totalActions = 0, onOpenActions }) => {
  const items = Array.isArray(actionSubtexts)
    ? actionSubtexts.filter(
        (item) =>
          item &&
          !String(item).toLowerCase().includes("open actie")
      )
    : [];
  const hasActions = Number(totalActions) > 0 || items.length > 0;
  const visibleItems = items.length > 0
    ? items
    : hasActions
      ? [
          t("parentPortal.summary.open_action", {
            defaultValue: "There is an open action",
          }),
        ]
      : [];

  if (!hasActions) {
    return (
      <div className="pp-mobile-section-card">
        <p className="pp-mobile-empty-state">
          {t("parentPortal.summary.no_open_actions", {
            defaultValue: "No open actions.",
          })}
        </p>
      </div>
    );
  }

  const openAction = (item, index) => {
    if (typeof onOpenActions === "function") {
      onOpenActions({ actionText: item, actionIndex: index });
    }
  };

  return (
    <div className="pp-mobile-section-card pp-mobile-actions-card">
      <ul
        className="pp-mobile-actions-list"
        aria-label={t("parentPortal.summary.actions_for_you", { defaultValue: "Actions for you" })}
      >
        {visibleItems.map((item, index) => (
          <li key={`${item}-${index}`}>
            <button
              type="button"
              className="pp-mobile-actions-item pp-mobile-clickable-row"
              onClick={() => openAction(item, index)}
            >
              <span className="pp-mobile-actions-icon" aria-hidden="true">
                <AlertIcon />
              </span>
              <span className="pp-mobile-actions-copy">
                <span className="pp-mobile-actions-title">{item}</span>
              </span>
              <ChevronRightIcon className="pp-mobile-item-chevron" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const SchoolCard = ({ t, parentData }) => {
  const schoolName = getSchoolValue(
    parentData,
    ["schoolName", "name", "displayName"],
    "de Werfklas"
  );

  const schoolStreet = getSchoolValue(
    parentData,
    ["schoolAddress", "address", "street"],
    "Parallelweg-Oost 23B"
  );
  const schoolPostalCity = [
    getSchoolValue(parentData, ["schoolPostalCode", "postalCode"], "4103 ND"),
    getSchoolValue(parentData, ["schoolCity", "city"], "Culemborg"),
  ]
    .filter(Boolean)
    .join(" ");
  const schoolAddress = [schoolStreet, schoolPostalCity].filter(Boolean).join("\n");

  const schoolPhone = getSchoolValue(parentData, ["schoolPhone", "phone"], "06-33846623");
  const schoolEmail = getSchoolValue(parentData, ["schoolEmail", "email"], "info@werfklas.nl");
  const schoolWebsite = getSchoolValue(
    parentData,
    ["schoolWebsite", "website"],
    "https://www.werfklas.nl"
  );

  return (
    <div className="pp-mobile-section-card">
      <dl className="pp-mobile-compact-list">
        <InfoItem icon={SchoolIcon} value={schoolName} compact />
        <InfoItem icon={MapPinIcon} value={schoolAddress} compact />
        <InfoItem icon={PhoneIcon} value={schoolPhone} compact />
        <InfoItem icon={MailIcon} value={schoolEmail} compact />
        <InfoItem icon={GlobeIcon} value={schoolWebsite} compact />
      </dl>

      <a
        className="pp-mobile-route-button"
        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          schoolAddress || schoolName
        )}`}
        target="_blank"
        rel="noreferrer"
      >
        <MapPinIcon aria-hidden="true" />
        {t("parentPortal.school.route", { defaultValue: "Route to school" })}
        <ExternalIcon aria-hidden="true" />
      </a>
    </div>
  );
};

const MyDetailsCard = ({ t, parentData, fullName, newsletterStatus, isNewsletterEligible }) => {
  const newsletterLabel = getNewsletterLabel({
    t,
    status: newsletterStatus,
    optedOut: parentData?.newsletterOptOut,
  });

  return (
    <div className="pp-mobile-section-card">
      <dl className="pp-mobile-compact-list">
        <InfoItem
          icon={UserIcon}
          value={fullName}
          compact
        />
        <InfoItem
          icon={PhoneIcon}
          value={parentData?.phone}
          compact
        />
        <InfoItem
          icon={MailIcon}
          value={parentData?.email}
          compact
        />
        <InfoItem
          icon={HomeIcon}
          value={[parentData?.address, parentData?.zipCode, parentData?.city]
            .filter(Boolean)
            .join("\n")}
          compact
        />
        {isNewsletterEligible && (
          <SimpleLine icon={MailIcon}>
            {t("parentPortal.newsletter.label", { defaultValue: "Newsletter" })}: {newsletterLabel}
          </SimpleLine>
        )}
      </dl>
    </div>
  );
};

const InfoTab = ({
  t,
  parentData,
  editMode,
  setEditMode,
  form,
  handleChange,
  handleSave,
  newsletterStatus,
  newsletterFirstSubscribed,
  isNewsletterEligible,
  nextCalendarEvent,
  upcomingCalendarEvents,
  formatCalendarDate,
  formatCalendarTime,
  getCalendarClassNames,
  actionSubtexts = [],
  totalActions = 0,
  onOpenActions,
  onOpenCalendar,
  onOpenFamily,
  editBankMode,
  setEditBankMode,
  bankForm,
  setBankForm,
  handleSubmitBankChange,
  bankAccount,
  hasPendingBankMutation,
}) => {
  const { i18n } = useTranslation();
  const dateLocale = i18n.resolvedLanguage === "en" || i18n.language?.startsWith("en") ? "en-US" : "nl-NL";
  const [detailsOpen, setDetailsOpen] = useState(false);
  const fullName = [parentData?.firstName, parentData?.lastName].filter(Boolean).join(" ");
  const normalizedActionSubtexts = (
    Array.isArray(actionSubtexts)
      ? actionSubtexts.filter(
          (item) =>
            item &&
            !String(item).toLowerCase().includes("open actie")
        )
      : []
  );

  const openActionCount =
    normalizedActionSubtexts.length > 0
      ? 1
      : Number(totalActions) > 0
        ? 1
        : 0;

  const actionsSubtitle = openActionCount
    ? t("parentPortal.summary.open_action", {
        defaultValue: "There is an open action",
      })
    : t("parentPortal.summary.no_open_actions", {
        defaultValue: "No open actions",
      });

  useEffect(() => {
    const openDetails = () => {
      setDetailsOpen(true);
      setEditMode?.(false);
    };

    window.addEventListener("parentPortal:openMyDetails", openDetails);
    return () => window.removeEventListener("parentPortal:openMyDetails", openDetails);
  }, [setEditMode]);

  if (detailsOpen || editMode) {
    return (
      <MyDetailsScreen
        t={t}
        parentData={parentData}
        form={form}
        handleChange={handleChange}
        handleSave={handleSave}
        editMode={editMode}
        setEditMode={setEditMode}
        fullName={fullName}
        newsletterStatus={newsletterStatus}
        isNewsletterEligible={isNewsletterEligible}
        onBack={() => setDetailsOpen(false)}
        onOpenFamily={() => {
          setDetailsOpen(false);
          setEditMode?.(false);
          onOpenFamily?.();
        }}
        editBankMode={editBankMode}
        setEditBankMode={setEditBankMode}
        bankForm={bankForm}
        setBankForm={setBankForm}
        handleSubmitBankChange={handleSubmitBankChange}
        bankAccount={bankAccount}
        hasPendingBankMutation={hasPendingBankMutation}
      />
    );
  }

  return (
    <div className="pp-mobile-info-layout pp-info-app-view" key="info-overview">
      <CollapsibleSection
        title={t("parentPortal.summary.upcoming_week", { defaultValue: "Upcoming week" })}
        subtitle={t("parentPortal.summary.upcoming_week_subtitle", {
          defaultValue: "Next activities",
        })}
        icon={CalendarIcon}
        defaultOpen={true}
        index={0}
      >
        <UpcomingCard
          t={t}
          nextCalendarEvent={nextCalendarEvent}
          upcomingCalendarEvents={upcomingCalendarEvents}
          formatCalendarDate={formatCalendarDate}
          formatCalendarTime={formatCalendarTime}
          getCalendarClassNames={getCalendarClassNames}
          onOpenCalendar={onOpenCalendar}
          dateLocale={dateLocale}
        />
      </CollapsibleSection>

      <CollapsibleSection
        title={t("parentPortal.summary.actions_for_you", { defaultValue: "Actions for you" })}
        subtitle={actionsSubtitle}
        icon={AlertIcon}
        defaultOpen={false}
        index={1}
        tone={openActionCount ? "warning" : "default"}
      >
        <ActionsCard
          t={t}
          actionSubtexts={normalizedActionSubtexts}
          totalActions={openActionCount}
          onOpenActions={onOpenActions}
        />
      </CollapsibleSection>

      <CollapsibleSection
        title={t("parentPortal.school.title", { defaultValue: "School details" })}
        subtitle={getSchoolValue(parentData, ["schoolName", "name", "displayName"], "de Werfklas")}
        icon={SchoolIcon}
        defaultOpen={false}
        index={2}
      >
        <SchoolCard t={t} parentData={parentData} />
      </CollapsibleSection>

      <CollapsibleSection
        title={t("parentPortal.my_details", { defaultValue: "My details" })}
        subtitle={fullName || t("parentPortal.my_details_subtitle", { defaultValue: "Contact and address" })}
        icon={UserIcon}
        defaultOpen={false}
        index={3}
      >
        <MyDetailsCard
          t={t}
          parentData={parentData}
          fullName={fullName}
          newsletterStatus={newsletterStatus}
          newsletterFirstSubscribed={newsletterFirstSubscribed}
          isNewsletterEligible={isNewsletterEligible}
        />

        <button type="button" className="pp-mobile-route-button pp-mobile-edit-details-button" onClick={() => setDetailsOpen(true)}>
          {t("parentPortal.edit_my_details", { defaultValue: "Edit my details" })}
          <ChevronRightIcon aria-hidden="true" />
        </button>
      </CollapsibleSection>
    </div>
  );
};

export default InfoTab;
